import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetTrigger,
} from '@mdvip/shared/components/blocks/ActionSheet';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { NetworkChip } from '@mdvip/shared/components/blocks/NetworkChip';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card } from '@mdvip/shared/components/ui/card';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { formatCityStateZip, formatExpiryFromParts } from '@/lib/payments/paymentAccount.format';

interface CreditCardRowProps {
  card: CreditCardAccount;
  onSetDefault: (id: string) => void;
  isSettingDefault?: boolean;
  onEdit: (id: string) => void;
  onRemove: (card: CreditCardAccount) => void;
  /** True when the card has a scheduled one-time payment — Remove is hidden, same as the default guard. */
  hasUpcomingPayment?: boolean;
}

export function CreditCardRow({
  card,
  onSetDefault,
  isSettingDefault = false,
  onEdit,
  onRemove,
  hasUpcomingPayment = false,
}: CreditCardRowProps) {
  const { t } = useTranslation('members');
  const canRemove = !card.isDefault && !hasUpcomingPayment;

  const number = `${card.network} ···· ${card.last4}`;
  const expLabel = card.isExpired
    ? t('payments.pages.paymentAccounts.creditCards.expiredBadge')
    : t('payments.pages.paymentAccounts.creditCards.expPrefix');
  const expiry = formatExpiryFromParts(card.expirationMonth, card.expirationYear);
  const typeLine = `${expLabel} ${expiry}`;
  const cityStateZip = formatCityStateZip(card.city, card.state, card.zip);
  const holderAddressLine = [card.holderName, cityStateZip].filter(Boolean).join(' · ');

  return (
    <Card
      className={cn(
        'flex-row! items-center gap-1! rounded-xl border py-0! pr-2 shadow-sm',
        card.isExpired ? 'border-destructive-foreground' : 'border-border',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5">
        <NetworkChip network={card.network} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-semibold text-foreground">{number}</span>
            {card.isDefault && !card.isExpired && (
              <Badge variant="success">
                <Icon name="circleCheck" size="xs" />
                {t('payments.pages.paymentAccounts.creditCards.defaultBadge')}
              </Badge>
            )}
            {card.isExpired && (
              <Badge variant="destructive">
                <Icon name="alertTriangle" size="xs" />
                {t('payments.pages.paymentAccounts.creditCards.expiredBadge')}
              </Badge>
            )}
          </div>
          <p
            className={cn(
              'mt-0.5 truncate text-xs',
              card.isExpired ? 'text-destructive-foreground' : 'text-muted-foreground',
            )}
          >
            {typeLine}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground sm:hidden">{holderAddressLine}</p>
        </div>

        <div className="hidden max-w-[44%] shrink-0 flex-col items-end pl-4 text-right sm:flex">
          <span className="truncate text-sm font-medium text-foreground">{card.holderName}</span>
          <span className="truncate text-xs text-muted-foreground">{cityStateZip}</span>
        </div>
      </div>

      <ActionSheet>
        <ActionSheetTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('payments.pages.paymentAccounts.creditCards.rowActionsLabel', { last4: card.last4 })}
              className="shrink-0 rounded-full text-muted-foreground"
            />
          }
        >
          <Icon name="ellipsisVertical" />
        </ActionSheetTrigger>
        <ActionSheetContent
          title={number}
          align="end"
          note={
            card.isDefault
              ? t('payments.pages.paymentAccounts.creditCards.defaultCannotRemove')
              : hasUpcomingPayment
                ? t('payments.pages.paymentAccounts.creditCards.upcomingPaymentCannotRemove')
                : undefined
          }
        >
          {!card.isDefault && !card.isExpired && (
            <ActionSheetItem
              icon={<Icon name="circleCheck" size="sm" />}
              onSelect={() => onSetDefault(card.id)}
              disabled={isSettingDefault}
            >
              {t('payments.pages.paymentAccounts.creditCards.setAsDefault')}
            </ActionSheetItem>
          )}
          <ActionSheetItem icon={<Icon name="pencil" size="sm" />} onSelect={() => onEdit(card.id)}>
            {t('payments.pages.paymentAccounts.creditCards.editDetails')}
          </ActionSheetItem>
          {canRemove && (
            <ActionSheetItem icon={<Icon name="trash" size="sm" />} onSelect={() => onRemove(card)} destructive>
              {t('payments.pages.paymentAccounts.creditCards.removeCard')}
            </ActionSheetItem>
          )}
        </ActionSheetContent>
      </ActionSheet>
    </Card>
  );
}
